import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from 'contexts/app';
import NotificationService from 'services/notification';
import UserService from 'services/user';

const notificationService = new NotificationService();
const userService = new UserService();

const EVENT_TYPES = [
  {
    event_type: 'ORDER_CREATED',
    label: 'Đơn hàng mới',
    description: 'Thông báo khi có đơn hàng mới được tạo thành công.',
  },
  {
    event_type: 'CUSTOMER_MARKED_PAID',
    label: 'Khách bấm đã thanh toán',
    description: 'Thông báo khi khách xác nhận đã thanh toán trên store.',
  },
];

const ROLE_OPTIONS = [
  { label: 'Owner', value: 'owner' },
  { label: 'Admin', value: 'admin' },
  { label: 'Staff', value: 'staff' },
  { label: 'CTV', value: 'ctv' },
];

const toRoleLabel = (role = '') => {
  const found = ROLE_OPTIONS.find((item) => item.value === role);
  return found?.label || role;
};

const useNotificationSettings = () => {
  const { message } = useApp();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);

  const userOptions = useMemo(() => {
    return (users || []).map((user) => ({
      value: user.id,
      label: `${user.full_name || user.phone || user.id} (${toRoleLabel(user?.work_space?.role || '')})`,
    }));
  }, [users]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rules, userRows] = await Promise.all([
        notificationService.listRules(),
        userService.list({}),
      ]);

      const ruleMap = new Map((rules || []).map((item) => [item.event_type, item]));
      const nextRows = EVENT_TYPES.map((eventItem) => {
        const rule = ruleMap.get(eventItem.event_type);
        return {
          key: eventItem.event_type,
          event_type: eventItem.event_type,
          label: eventItem.label,
          description: eventItem.description,
          enabled: rule?.enabled !== false,
          roles: Array.isArray(rule?.roles) && rule.roles.length > 0 ? rule.roles : ['owner', 'admin'],
          recipient_ids: Array.isArray(rule?.recipient_ids) ? rule.recipient_ids : [],
        };
      });

      setRows(nextRows);
      setUsers(userRows || []);
    } catch (error) {
      message.error(error?.message || 'Không thể tải cấu hình nhận thông báo');
      setRows([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateRow = useCallback((eventType, patch) => {
    setRows((prev) => prev.map((item) => (item.event_type === eventType ? { ...item, ...patch } : item)));
  }, []);

  const saveOne = useCallback(async (row) => {
    await notificationService.saveRule({
      event_type: row.event_type,
      recipient_ids: row.recipient_ids || [],
      roles: row.roles || [],
      enabled: row.enabled !== false,
    });
  }, []);

  const saveAll = useCallback(async () => {
    setSubmitting(true);
    try {
      await Promise.all((rows || []).map((row) => saveOne(row)));
      message.success('Đã lưu cấu hình nhận thông báo');
      await loadData();
    } catch (error) {
      message.error(error?.message || 'Lưu cấu hình thất bại');
    } finally {
      setSubmitting(false);
    }
  }, [loadData, message, rows, saveOne]);

  return {
    loading,
    submitting,
    rows,
    roleOptions: ROLE_OPTIONS,
    userOptions,
    loadData,
    updateRow,
    saveAll,
  };
};

export default useNotificationSettings;
